import React, { useState } from 'react';
import styled from 'astroturf/react';
import { PaperUpdateUnlinked, Paper } from 'js/social/shared/types';
import { NoThumbnailIcon } from 'js/social/shared/Icons';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/social/shared/design_system/Button';
import DownloadIcon from 'js/design_system/icons/arrow_downward.svg';
import PlusIcon from 'js/design_system/icons/add.svg';
import CheckIcon from 'js/design_system/icons/check.svg';
import ImpressionWatcher, {
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';
import { Reader } from 'js/social/Reader/Reader';
import useScrollLock from 'js/utils/hooks/useScrollLock';
import { SimplePaperPodcast } from 'js/podcast_ai/SimplePaperPodcast';

export type PaperSize = 'xsmall' | 'small' | 'medium' | 'large';

const Container = styled.div`
  word-break: break-word;
`;

const Metadata = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  overflow: hidden;
`;

const PaperTitle = styled(Body)<{ paperSize?: PaperSize }>`
  @import 'stylesheets/design_system/_util.scss';
  text-align: left;

  &:not(.paperSize-small):not(.paperSize-xsmall) {
    @include text-ellipsis(2, 20px);
  }

  &.paperSize-small {
    @include text-ellipsis(2, 18px);
    font-size: 12px;
    line-height: 18px;
  }

  &.paperSize-xsmall {
    @include text-ellipsis(1, 18px);
    font-size: 12px;
    line-height: 18px;
  }
`;

const MetadataText = styled(Body)`
  @import 'stylesheets/design_system/_util.scss';

  // Webkit browsers (Chrome, Safari, Edge)
  @supports (-webkit-line-clamp: 1) or (line-clamp: 1) {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    line-clamp: 1;
  }

  // Fallback for non-webkit browsers
  @supports (not (-webkit-line-clamp: 1)) and (not (line-clamp: 1)) {
    @include text-ellipsis(1, 18px);
  }
`;

const PaperThumbnail = styled.div<{ size?: PaperSize }>`
  flex-shrink: 0;
  width: 48px;
  height: 62px;
  border-radius: 2.5px;

  @include for-tablet-up {
    width: 62px;
    height: 80px;
  }

  &.size-medium {
    height: 62px;
    width: 48px;
  }

  &.size-small {
    width: 16px;
    height: 20px;

    img,
    svg {
      width: 16px;
      height: 20px;
    }
  }

  &.size-xsmall {
    width: 15px;
    height: 18px;

    img,
    svg {
      width: 15px;
      height: 18px;
    }
  }

  img,
  svg {
    width: 48px;
    height: 62px;
    border-radius: 2.5px;
    max-width: 100%;
    max-height: 100%;
    filter: drop-shadow(0px 1.25593px 1.25593px rgba(0, 0, 0, 0.115))
      drop-shadow(0px 2.51186px 1.25593px rgba(0, 0, 0, 0.09))
      drop-shadow(0px 1.25593px 3.76779px rgba(0, 0, 0, 0.15));

    @include for-tablet-up {
      width: 62px;
      height: 80px;
    }
  }
`;

const ContentContainer = styled.div<{ size?: PaperSize }>`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;

  @include for-tablet-up {
    flex-direction: row;

    &.size-medium {
      max-width: calc(100% - 48px - 16px);
    }

    &.size-large {
      max-width: calc(100% - 62px - 16px);
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;

  > div > button {
    width: 100%;
  }

  @include for-tablet-up {
    flex-direction: column;
  }
`;

const PaperCard = styled.div<{ size?: PaperSize }>`
  display: flex;
  flex-direction: column;
  padding: 12px 12px 12px 16px;
  gap: 16px;
  color: $ds-black;
  background-color: #f9f7f4;
  border: 1px solid #cdc5af;
  border-left-width: 4px;
  border-radius: 4px;

  &:hover ${PaperTitle} {
    text-decoration: underline;
  }

  &.size-small {
    height: 46px;
    padding: 4px 4px 4px 8px;
    gap: 8px;
  }

  &.size-xsmall {
    height: 35px;
    padding: 8px;
  }
`;

const PaperInfo = styled.div`
  display: flex;
  gap: 16px;
`;

type Props = {
  work: PaperUpdateUnlinked;
  showBookmark?: boolean;
  showDownload?: boolean;
  showInlineReader?: boolean;
  isBookmarked?: boolean;
  downloadUrl?: string;
  size?: PaperSize;
  style?: React.CSSProperties;
  className?: string;
  onDownload?: () => void;
  onBookmark?: () => void;
  onUnbookmark?: () => void;
  impressionProps?: ImpressionProps;
  onsitePodcastPromotion?: boolean;
  onPodcastReady?: () => void;
};

// Type guard to check if work has an id
const hasId = (work: Paper | PaperUpdateUnlinked): work is Paper => {
  return 'id' in work && typeof (work as { id?: unknown }).id === 'number';
};

const PaperContainer: React.FunctionComponent<Props> = ({
  work,
  showBookmark = false,
  showDownload = false,
  showInlineReader = false,
  isBookmarked,
  downloadUrl,
  size = 'large',
  style,
  className,
  onDownload = () => null,
  onBookmark = () => null,
  onUnbookmark = () => null,
  impressionProps,
  onsitePodcastPromotion = false,
  onPodcastReady,
}) => {
  const { url, title, authors, publicationYear, publication, attachment } =
    work;
  const [loading, setLoading] = useState(false);
  const [showReader, setShowReader] = useState(false);

  const showAllContent = size === 'medium' || size === 'large';
  const publicationInfo: string[] = [];
  if (publicationYear) publicationInfo.push(publicationYear.toString());
  if (publication) publicationInfo.push(publication);

  useScrollLock(showReader);

  const handleDownload = async () => {
    window.open(downloadUrl, '_blank')?.focus();
    await onDownload();
  };

  const handleBookmark = async (callback: () => void) => {
    try {
      setLoading(true);
      await callback();
    } finally {
      setLoading(false);
    }
  };

  let { source } = impressionProps || {};
  source = source || 'paperContainer';

  const content = (
    <PaperCard size={size}>
      <PaperInfo>
        <PaperThumbnail size={size}>
          {attachment?.thumbnailUrl != null &&
          attachment.thumbnailUrl !== '' ? (
            <img src={attachment.thumbnailUrl} alt="" />
          ) : (
            <NoThumbnailIcon />
          )}
        </PaperThumbnail>

        <ContentContainer size={size}>
          <Metadata>
            <PaperTitle size="sm" bold paperSize={size}>
              {title}
            </PaperTitle>

            {showAllContent && (
              <div>
                {authors?.length > 0 && (
                  <MetadataText size="xs">
                    by {authors.map((p) => p.displayName).join(', ')}
                  </MetadataText>
                )}
                {publicationInfo.length > 0 && (
                  <MetadataText size="xs">
                    {publicationInfo.join(' • ')}
                  </MetadataText>
                )}
              </div>
            )}
          </Metadata>

          {showAllContent && (
            <ButtonContainer>
              {showDownload && downloadUrl && (
                <ImpressionWatcher
                  {...impressionProps}
                  source={`${source}_download-button`}
                >
                  <Button
                    icon={<DownloadIcon size={18} />}
                    variant="inverse-gray"
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                </ImpressionWatcher>
              )}
              {showBookmark && (
                <ImpressionWatcher
                  {...impressionProps}
                  source={`${source}_bookmark-button`}
                >
                  {isBookmarked ? (
                    <Button
                      icon={<CheckIcon size={18} />}
                      variant="inverse-gray"
                      disabled={loading}
                      onClick={() => handleBookmark(onUnbookmark)}
                    >
                      Bookmarked
                    </Button>
                  ) : (
                    <Button
                      icon={<PlusIcon size={18} />}
                      variant="inverse-gray"
                      disabled={loading}
                      onClick={() => handleBookmark(onBookmark)}
                    >
                      Bookmark
                    </Button>
                  )}
                </ImpressionWatcher>
              )}
            </ButtonContainer>
          )}
        </ContentContainer>
      </PaperInfo>

      {onsitePodcastPromotion && hasId(work) && (
        <SimplePaperPodcast
          workId={work.id}
          workTitle={work.title}
          showTitle={false}
          impressionSource="paper_to_podcast_newsfeed"
          onsitePodcastPromotion={true}
        />
      )}

      {work.podcast && (
        <SimplePaperPodcast
          workId={work.podcast.workId}
          workTitle={work.podcast.workTitle}
          showTitle={false}
          impressionSource="paper_to_podcast_newsfeed"
          onPlayerReady={onPodcastReady}
        />
      )}
    </PaperCard>
  );

  return (
    <Container style={style} className={className}>
      {showInlineReader && attachment?.readable && showReader && (
        <Reader
          open
          work={work}
          impressionProps={impressionProps}
          onDownload={handleDownload}
          onClose={() => setShowReader(false)}
        />
      )}

      {url ? (
        <a
          href={url}
          style={{ textDecoration: 'none' }}
          target={'_blank'}
          // Always track impressions, but stop opening the link if button got pressed
          onClick={(event) => {
            if (
              event.target instanceof Element &&
              event.target.closest('button') !== null
            ) {
              // Stop link opening if paper button got clicked
              event.preventDefault();
              event.stopPropagation();
            } else if (showInlineReader && attachment?.readable) {
              event.preventDefault();
              setShowReader(true);
            }
          }}
        >
          {content}
        </a>
      ) : (
        content
      )}
    </Container>
  );
};

export default PaperContainer;
